import Vue from 'vue';
import VueRouter from 'vue-router';
import routes from '../constants/routes';
/* eslint-disable-next-line */
import store from '../store';

if (import.meta.env.NODE_ENV !== 'test') {
  Vue.use(VueRouter);
}

// https://router.vuejs.org/guide/advanced/scroll-behavior.html#async-scrolling
const scrollBehavior = (to, from, savedPosition) => {
  if (to.path === '/market-report') {
    // Don't scroll to top when only query params change
    // See: https://github.com/vuejs/vue-router/issues/2072
    if (from && Object.keys(to.query).length) {
      if (to.fullPath.split('?')[0] === from.fullPath.split('?')[0]) {
        return null;
      }
    }
  }

  if (['Search', 'Marketplace'].includes(from.name)
    && [('Search', 'Marketplace')].includes(to.name)
  ) {
    return savedPosition;
  }

  const resetCoordinates = { x: 0, y: 0 };
  return savedPosition || resetCoordinates;
};

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior,
});

const setPageTitle = (to) => {
  const titlePrefix = 'ACV Auctions';

  // if the route has a title specified, let's use it
  if (to.meta.title) {
    document.title = `${titlePrefix} | ${to.meta.title}`;
  } else {
    document.title = titlePrefix;
  }
};

const routeUser = (to, from, next) => {
  const { isAuthorized } = store.getters;
  const { canSell } = store.getters;

  const { name, fullPath } = to;

  // for auction-viewing routes, keep a record of the auction route to login with
  // otherwise, clear stored auction route
  if (['auction', 'saved', 'Capital Fees', 'Search', 'Marketplace'].includes(name)) {
    store.dispatch('setGoToAfterLogin', fullPath);
  } else if (!['login', 'logout'].includes(name)) {
    store.dispatch('clearGoToAfterLogin');
  }

  if (
    !to.meta.allowUnauthorized
    && !isAuthorized
  ) {
    // navigate to logout in order to route to login
    router.push('/logout');
  }

  if (to.meta.sellerOnly && !canSell) {
    next('/auctions/active');
  }

  const hasOutstandingAuctions = store.getters['auctions/hasOutstandingAuctions'];
  if (hasOutstandingAuctions) {
    // Allow them to logout or edit their settings. Everything else should
    // be redirected back to outstanding auctions - ETC
    const goingToSettingsOrLogout = to.path.includes('settings') || to.name === 'logout';

    if (goingToSettingsOrLogout) {
      next();
      return;
    }

    if (to.name === 'auction') {
      const auctionId = parseInt(to.params.id, 10);
      const isOutstandingAuction = store.getters['auctions/isOutstandingAuction'](auctionId);
      if (isOutstandingAuction) {
        next();
        return;
      }
    }

    if (from.name === 'BuyingOutstanding') {
      next(false);
      return;
    }

    if (to.name !== 'BuyingOutstanding') {
      next({ name: 'BuyingOutstanding', replace: true });
      return;
    }
  }

  // if we don't match a route in vue, send to nginx/web-php via browser
  if (to.matched.length) {
    setPageTitle(to);
    next();
  } else {
    window.location.replace(to.fullPath);
  }
};

router.beforeEach((to, from, next) => {
  const { authorizationIsReady } = store.getters;
  if (!authorizationIsReady) {
    // eslint-disable-next-line
    store._vm.$root.$on('storageReady', async () => {
      await store.dispatch('updateReady', true);
      routeUser(to, from, next);
    });
  } else {
    routeUser(to, from, next);
  }
});

router.afterEach((to, from) => {
  if ((from.name === 'auction' || from.name === 'saved')
    && !Object.keys(from.query).includes('action')) {
    store.dispatch('marketplaceAuctions/clearViewingAuctionModel');
  }
});

export default router;
